import {
  confirmTwoFactorAuthentication,
  resendOtp,
  startPolicyReviewEligibility,
} from "@/api"
import { useState } from "react"
import { StartPolicyReviewRequest } from "../../../../api/hcsraCustomApi"
import { FormData } from "../PolicyForm"

export type FormState =
  | "idle"
  | "submitting"
  | "otpSent"
  | "otpVerifying"
  | "success"
  | "error"
  | "otpError"
  | "otpExpired"

export const usePolicyReviewEligibility = (data: any) => {
  // Form states
  const [formState, setFormState] = useState<FormState>("idle")
  const [phoneNumber, setPhoneNumber] = useState("")
  const [otpError, setOtpError] = useState<string | undefined>(undefined)
  const [requestId, setRequestId] = useState<string | undefined>(undefined)

  // Form submission
  const handleFormSubmit = async (formData: FormData) => {
    try {
      setFormState("submitting")
      setPhoneNumber(formData.phoneNumber)

      // Split phone number into prefix and main number
      // Assuming the first 3 digits are the prefix (050, 052, etc)
      const prefixPhoneNumber = formData.phoneNumber.substring(0, 3)
      const mainPhoneNumber = formData.phoneNumber.substring(3)

      // Prepare request data
      const requestData: StartPolicyReviewRequest = {
        prefixPhoneNumber,
        phoneNumber: mainPhoneNumber,
        email: formData.email,
        identityNumber: formData.id,
        policyNumber: formData.policyNumber,
        licenseNumber: formData.licensePlate,
        checkBoxVerification: formData.agreeTerms,
      }

      // Send data to the backend API
      const response = await startPolicyReviewEligibility(requestData)

      // Store the request ID for OTP verification
      setRequestId(response.policyReviewEligibilityRequestId)
      setFormState("otpSent")
    } catch (error) {
      console.error("Error submitting form:", error)
      setFormState("error")
    }
  }

  // OTP verification
  const handleOtpSubmit = async (otp: string) => {
    try {
      if (!requestId) {
        throw new Error("Missing request ID")
      }

      setFormState("otpVerifying")
      setOtpError(undefined)

      // Send OTP verification to the backend
      const response = await confirmTwoFactorAuthentication({
        policyReviewEligibilityRequestId: requestId,
        otpCode: otp,
      })

      if (response.isOtpVerified) {
        setFormState("success")
      } else {
        // OTP invalid
        setOtpError(data.errorCodeText?.value)
        setFormState("otpError")
      }
    } catch (error) {
      // Check if it's an expired OTP error
      // This would need to be adjusted based on your API's error response structure
      const isExpiredError = false // Replace with actual logic to detect expired OTP errors

      if (isExpiredError) {
        setOtpError(data.invalidCodeText?.value)
        setFormState("otpExpired")
      } else {
        setOtpError(data.errorCodeText?.value)
        setFormState("otpError")
      }
    }
  }

  // Resend OTP
  const handleResendOtp = async () => {
    try {
      if (!requestId) {
        throw new Error("Missing request ID")
      }

      // setFormState("submitting")
      setOtpError(undefined)

      // Request OTP resend from the backend
      await resendOtp({
        policyReviewEligibilityRequestId: requestId,
      })

      setFormState("otpSent")
    } catch (error) {
      setFormState("error")
    }
  }

  // Reset the form state
  const handleRetry = () => {
    setFormState("idle")
    setOtpError(undefined)
    setRequestId(undefined)
  }

  return {
    formState,
    phoneNumber,
    otpError,
    requestId,
    handleFormSubmit,
    handleOtpSubmit,
    handleResendOtp,
    handleRetry,
  }
}

export default usePolicyReviewEligibility
